import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { useLocation } from '../hooks/useLocation';
import { Grid } from '../components/ui/Grid';
import { Users, UserPlus, Plus, MapPin, Edit, Trash2, Music2, User, Music, X, Calendar, ExternalLink, Star } from 'lucide-react';
import { format } from 'date-fns';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { StoryModal } from '../components/StoryModal';
import { StoryPanel } from '../components/StoryPanel';
import { EventPanel } from '../components/EventPanel';
import { ActionMenu } from '../components/ui/ActionMenu';
import { ConfirmationDialog } from '../components/ui/ConfirmationDialog';
import { Modal } from '../components/ui/Modal';
import { InviteMemberForm } from '../components/InviteMemberForm';
import { SoundForm } from '../components/SoundForm';
import { SortableSoundList } from '../components/SortableSoundList';
import { ReleasesSlider } from '../components/ReleasesSlider';
import { SEOHead } from '../components/SEOHead';
import { FavoriteBandButton } from '../components/FavoriteBandButton';
import { FanAreaModal } from '../components/FanAreaModal';
import { EditMemberForm } from '../components/EditMemberForm';
import { ReleaseForm } from '../components/ReleaseForm';
import { QRFlyerModal } from '../components/QRFlyerModal';
import { Heart } from 'lucide-react';
import { fetchBandDetails, deleteBand, type BandWithDetails } from '../services/api/band';
import { updateSoundOrder, fetchSounds } from '../services/api/sounds';
import { fetchReleases, updateReleaseOrder } from '../services/api/releases';
import { formatDistance } from '../utils/distance';
import { useMusicTranslations } from '../utils/translations';
import { supabase } from '../lib/supabase';
import type { Sound, FanSubscription } from '../types/database';
import type { Release } from '../types/database';
import { QrCode } from 'lucide-react';

function BandDetails() {
  const { t } = useTranslation();
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const { coordinates } = useLocation();
  const { translateGenre, translateBandStatus, translateSkill } = useMusicTranslations();
  const [band, setBand] = useState<BandWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMember, setIsMember] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedStory, setSelectedStory] = useState<any>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAddSoundModal, setShowAddSoundModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sounds, setSounds] = useState<Sound[]>([]);
  const [releases, setReleases] = useState<Release[]>([]);
  const [playingSound, setPlayingSound] = useState<string | null>(null);
  const [showEditSoundModal, setShowEditSoundModal] = useState<Sound | null>(null);
  const [showDeleteSoundConfirm, setShowDeleteSoundConfirm] = useState<string | null>(null);
  const [showRemoveMemberConfirm, setShowRemoveMemberConfirm] = useState<{ id: string; name: string } | null>(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [showFanAreaModal, setShowFanAreaModal] = useState(false);
  const [userSubscription, setUserSubscription] = useState<FanSubscription | null>(null);
  const [loadingSubscription, setLoadingSubscription] = useState(false);
  const [showEditMemberModal, setShowEditMemberModal] = useState<{
    profile: any;
    role: string;
    instruments: string[];
  } | null>(null);
  const [showAddReleaseModal, setShowAddReleaseModal] = useState(false);
  const [showEditReleaseModal, setShowEditReleaseModal] = useState<Release | null>(null);
  const [showDeleteReleaseConfirm, setShowDeleteReleaseConfirm] = useState<string | null>(null);
  const [showQRFlyerModal, setShowQRFlyerModal] = useState(false);
  const deleteBandWarningItems = t('bands.deleteBandWarningItems', { returnObjects: true });

  const handleReleasesReorder = async (reorderedReleases: Release[]) => {
    try {
      setSavingOrder(true);
      
      // Update local state immediately for responsive UI
      setReleases(reorderedReleases);
      
      // Add order field to each release
      const releasesWithOrder = reorderedReleases.map((release, index) => ({
        ...release,
        order: index
      }));
      
      // Update order in database
      await updateReleaseOrder(releasesWithOrder);
    } catch (err) {
      console.error('Error reordering releases:', err);
      setError(err instanceof Error ? err.message : 'Failed to reorder releases');
    } finally {
      setSavingOrder(false);
    }
  };

  useEffect(() => {
    async function loadBandDetails() {
      if (!slug) return;

      try {
        const bandData = await fetchBandDetails(slug, coordinates);
        setBand(bandData);

        // Check if current user is member or admin
        if (profile) {
          const userMember = bandData.members.find(m => m.profile.id === profile.id);
          setIsMember(!!userMember);
          setIsAdmin(userMember?.role === 'admin');
        }

        // Load sounds
        const soundsData = await fetchSounds(bandData.id);
        setSounds(soundsData);

        // Load releases
        const releasesData = await fetchReleases(bandData.id);
        setReleases(releasesData);

        // Check user's subscription status if logged in
        if (profile) {
          setLoadingSubscription(true);
          try {
            const { data: subscription } = await supabase
              .from('fan_subscriptions')
              .select('*')
              .eq('user_id', profile.id)
              .eq('band_id', bandData.id)
              .in('status', ['completed', 'active'])
              .maybeSingle();

            setUserSubscription(subscription);
          } catch (err) {
            console.error('Error checking subscription:', err);
          } finally {
            setLoadingSubscription(false);
          }
        }

        // Check for successful fan subscription from URL params
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('support_success') === 'true' && profile) {
          console.log('Support success detected, checking subscription status...');
          
          // Single check for subscription after successful payment
          const checkSubscription = async () => {
            try {
              console.log('Checking subscription status after successful payment...');
              const { data: subscription } = await supabase
                .from('fan_subscriptions')
                .select('*')
                .eq('user_id', profile.id)
                .eq('band_id', bandData.id)
                .in('status', ['completed', 'active'])
                .maybeSingle();

              console.log('Subscription check result:', subscription);
              
              if (subscription) {
                setUserSubscription(subscription);
              }
              
              // Clean up URL regardless of result
              const newUrl = window.location.pathname;
              window.history.replaceState({}, '', newUrl);
            } catch (err) {
              console.error('Error checking subscription after payment:', err);
              // Clean up URL even on error
              const newUrl = window.location.pathname;
              window.history.replaceState({}, '', newUrl);
            }
          };
          
          // Wait 3 seconds for webhook to process, then check once
          setTimeout(checkSubscription, 3000);
        }
      } catch (err) {
        console.error('Error loading band details:', err);
        setError(err instanceof Error ? err.message : t('bands.bandNotFound'));
      } finally {
        setLoading(false);
      }
    }

    loadBandDetails();
  }, [slug, profile, coordinates, t]);

  const handleDelete = async () => {
    if (!band) return;

    try {
      setIsDeleting(true);
      await deleteBand(band.id);
      navigate('/bands');
    } catch (err) {
      console.error('Error deleting band:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete band');
      setIsDeleting(false);
    }
  };

  const handleDeleteSound = async (soundId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('sounds')
        .delete()
        .eq('id', soundId);

      if (deleteError) throw deleteError;

      setSounds(prev => prev.filter(s => s.id !== soundId));
      setShowDeleteSoundConfirm(null);
    } catch (err) {
      console.error('Error deleting sound:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete sound');
    }
  };

  const handleRemoveMember = async () => {
    if (!band || !showRemoveMemberConfirm) return;

    try {
      const { error: deleteError } = await supabase
        .from('band_members')
        .delete()
        .eq('band_id', band.id)
        .eq('profile_id', showRemoveMemberConfirm.id);

      if (deleteError) throw deleteError;

      // Update the local state
      setBand(prev => {
        if (!prev) return null;
        return {
          ...prev,
          members: prev.members.filter(m => m.profile.id !== showRemoveMemberConfirm.id)
        };
      });

      setShowRemoveMemberConfirm(null);
    } catch (err) {
      console.error('Error removing member:', err);
      setError(err instanceof Error ? err.message : 'Failed to remove member');
    }
  };

  const handleUpdateSound = async (sound: Sound, updates: Partial<Sound>) => {
    try {
      const { error: updateError } = await supabase
        .from('sounds')
        .update(updates)
        .eq('id', sound.id);

      if (updateError) throw updateError;

      setSounds(prev => prev.map(s => s.id === sound.id ? { ...s, ...updates } : s));
      setShowEditSoundModal(null);
    } catch (err) {
      console.error('Error updating sound:', err);
      setError(err instanceof Error ? err.message : 'Failed to update sound');
    }
  };

  const handleSoundsReorder = async (reorderedSounds: Sound[]) => {
    try {
      setSavingOrder(true);
      
      // Update local state immediately for responsive UI
      setSounds(reorderedSounds);
      
      // Add order field to each sound
      const soundsWithOrder = reorderedSounds.map((sound, index) => ({
        ...sound,
        order: index
      }));
      
      // Update order in database
      await updateSoundOrder(soundsWithOrder);
    } catch (err) {
      console.error('Error reordering sounds:', err);
      setError(err instanceof Error ? err.message : 'Failed to reorder sounds');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDeleteRelease = async (releaseId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('releases')
        .delete()
        .eq('id', releaseId);

      if (deleteError) throw deleteError;

      setReleases(prev => prev.filter(r => r.id !== releaseId));
      setShowDeleteReleaseConfirm(null);
    } catch (err) {
      console.error('Error deleting release:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete release');
    }
  };

  const bandActions = isAdmin ? [
    {
      label: t('bands.editBand'),
      icon: <Edit className="w-4 h-4" />,
      onClick: () => navigate(`/bands/${band?.slug}/edit`),
    },
    ...(band?.avatar_url ? [{
      label: 'Show QR-Flyer',
      icon: <QrCode className="w-4 h-4" />,
      onClick: () => setShowQRFlyerModal(true),
    }] : []),
    ...(band.fan_area_enabled ? [{
      label: t('fanArea.fanContent'),
      icon: <Star className="w-4 h-4" />,
      onClick: () => navigate(`/bands/${band.slug}/manage-fan-content`)
    }] : []),
    {
      label: t('bands.deleteBand'),
      icon: <Trash2 className="w-4 h-4" />,
      onClick: () => setShowDeleteConfirm(true),
      className: 'text-red-500 hover:text-red-500',
    }
  ] : [];

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-24">
        <div className="text-center text-light/60">{t('bands.loadingBandDetails')}</div>
      </div>
    );
  }

  if (error || !band) {
    return (
      <div className="container mx-auto px-6 py-24">
        <div className="text-center text-red-500">{error || t('bands.bandNotFound')}</div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: t('nav.bands'), href: '/bands' },
    { label: band.name },
  ];

  // Generate structured data for the band
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MusicGroup",
    "name": band.name,
    "description": band.description,
    "image": band.avatar_url,
    "url": `https://jamnation.ch/bands/${band.slug}`,
    "genre": band.genres,
    "location": band.location ? {
      "@type": "Place",
      "name": band.location,
      ...(band.coordinates && {
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": band.coordinates.latitude,
          "longitude": band.coordinates.longitude
        }
      })
    } : undefined,
    "member": band.members.map(member => ({
      "@type": "Person",
      "name": member.profile.artist_name || member.profile.full_name,
      "url": `https://jamnation.ch/musicians/${member.profile.slug}`,
      "roleName": member.instruments.join(', ')
    })),
    "event": band.events.map(event => ({
      "@type": "MusicEvent",
      "name": event.title,
      "startDate": event.event_date,
      "location": {
        "@type": "Place",
        "name": event.location,
        ...(event.coordinates && {
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": event.coordinates.latitude,
            "longitude": event.coordinates.longitude
          }
        })
      },
      "performer": {
        "@type": "MusicGroup",
        "name": band.name
      }
    }))
  };

  return (
    <>
      <SEOHead
        title={`${band.name} | Band aus ${band.location || 'der Schweiz'}`}
        description={`Entdecke ${band.name}${band.location ? ` aus ${band.location}` : ''}. ${band.description || `Eine ${band.genres?.join(', ') || 'Schweizer'} Band auf jamnation.`} Erfahre mehr über die Bandmitglieder, Events und Stories.`}
        keywords={[
          band.name,
          ...(band.genres || []),
          ...(band.location ? [band.location, `Band ${band.location}`] : []),
          'Schweizer Band',
          'Musik Schweiz',
          'Band Schweiz'
        ]}
        url={`https://jamnation.ch/bands/${band.slug}`}
        image={band.avatar_url}
        type="music.album"
        structuredData={structuredData}
      />

      <div className="container mx-auto px-6 pb-8 md:py-24">
        <div className="mb-6">
          <Breadcrumbs items={breadcrumbItems} />
        </div>

         <motion.div 
          className="max-w-4xl mx-auto"
          initial={{ opacity: 0, x: 200 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="panel mb-8 relative">
            {isAdmin && (
              <div className="absolute top-4 right-4 z-10">
                <ActionMenu items={bandActions} />
              </div>
            )}
            {band.fan_area_enabled && user && profile && !loadingSubscription && !isMember && (
              <div className="absolute top-6 right-6 z-10">
                {userSubscription ? (
                  <Link
                    to={`/bands/${band.slug}/fan-content`}
                    className="underline flex items-center space-x-2"
                  >
                    <Star className="w-5 h-5" />
                    <span>{t('fanArea.fanContent')}</span>
                  </Link>
                ) : (
                  <button
                    onClick={() => setShowFanAreaModal(true)}
                    className="underline flex items-center space-x-2"
                  >
                    <Star className="w-5 h-5" />
                    <span>{t('fanArea.becomeFan')}</span>
                  </button>
                )}
              </div>
            )}
            <div className="flex flex-col md:flex-row gap-6 mb-6">
              <div className="w-100 sm:w-40 aspect-square bg-gray-custom/10 flex items-center justify-center overflow-hidden">
                {band.avatar_url ? (
                  <img
                    src={band.avatar_url}
                    alt={band.name}
                    className="w-full h-full object-cover"
                   loading="lazy" />
                ) : (
                  <Users className="w-12 h-12 text-light/60" />
                )}
              </div>
              <div className="flex flex-col justify-center">
                <div className="flex items-center gap-3">
                  <h1 className="text-3xl font-bold text-light">{band.name}</h1>
                  {user && profile && !profile.is_musician && (
                    <FavoriteBandButton bandId={band.id} />
                  )}
                </div>
                {band.location && (
                  <div className="flex items-center text-light/40 mt-2">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span>{band.location}</span>
                    {band.distance !== undefined && (
                      <span className="text-primary ml-2">
                        {formatDistance(band.distance)}
                      </span>
                    )}
                  </div>
                )}
                {band.website_url && (
                  <div className="flex items-center text-light/40 hover:text-light transition-colors mt-1">
                    <ExternalLink className="w-4 h-4 mr-1" />
                    <Link target="_blank" to={band.website_url}>
                      <span>{band.website_url}</span>
                    </Link>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-12 mt-6">
              {band.genres && band.genres.length > 0 && (
                <div>
                  <h2 className="font-semibold text-xl mb-3">
                    {t('musician.genres')}
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {band.genres.map((genre) => (
                      <span
                        key={genre}
                        className="px-3 py-1 bg-tertiary rounded-full text-light"
                      >
                        {translateGenre(genre)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {/* TODO: Re-set this with actual band status */}
              <div>
                <h2 className="font-semibold text-xl mb-3">
                  Status
                </h2>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-primary rounded-full text-light">{translateBandStatus(band.status)}</span>
                </div>
              </div>
              {band.foundation_year && (
                <div>
                  <h2 className="font-semibold text-xl mb-3">
                    {t('common.founded')}
                  </h2>
                  <div>
                    <span className="text-light">
                      {band.foundation_year}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Band Description Section */}
          {band.description && (
            <div className="mb-8">
              <div>
                <div className="border-l border-solid border-primary pl-6 text-light/60 whitespace-pre-line">{band.description}</div>
              </div>
            </div>
          )}

          {/* Band Members Section */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-6 relative">
              <h2 className="text-2xl font-semibold">{t('bands.members.title')}</h2>
              {isAdmin && (
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="btn-outline absolute right-0 flex items-center space-x-2"
                >
                  <UserPlus className="w-5 h-5" />
                  <span className="hidden md:inline">{t('bands.inviteMembers')}</span>
                </button>
              )}
            </div>
            <Grid gap={6} cols={4}>
              {band.members.map((member) => (
                <div key={member.profile.id} className="relative group">
                  <Link
                    to={`/musicians/${member.profile.slug}`}
                    className="block panel hover:bg-gray-custom/20 text-center"
                  >
                    <div className="flex flex-col items-center">
                      <div className="w-32 h-32 rounded-full bg-gray-custom/10 flex items-center justify-center overflow-hidden mb-4">
                        {member.profile.avatar_url ? (
                          <img
                            src={member.profile.avatar_url}
                            alt={member.profile.artist_name || ''}
                            className="w-full h-full object-cover"
                           loading="lazy" />
                        ) : (
                          <Users className="w-8 h-8 text-light/60" />
                        )}
                      </div>
                      <h3 className="font-semibold text-xl">{member.profile.artist_name}</h3>
                      {isAdmin && (<p className="text-sm text-light/60 mt-2">{member.role}</p>)}
                      <div className="flex flex-wrap justify-center gap-1 mt-3">
                        {member.instruments.map((instrument) => (
                          <span
                            key={instrument}
                            className="px-2 py-0.5 bg-secondary rounded-full text-xs text-light"
                          >
                            {translateSkill(instrument)}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                  {isAdmin && member.role !== 'admin' && (
                    <div className="absolute top-2 right-2 z-10">
                      <ActionMenu
                        items={[
                          {
                            label: 'Edit Member',
                            icon: <Edit className="w-4 h-4" />,
                            onClick: () => setShowEditMemberModal(member),
                          },
                          {
                            label: t('bands.members.removeMember'),
                            icon: <Trash2 className="w-4 h-4" />,
                            onClick: () => setShowRemoveMemberConfirm({
                              id: member.profile.id,
                              name: member.profile.artist_name || 'Unknown'
                            }),
                            className: 'text-red-500 hover:text-red-500',
                          },
                        ]}
                      />
                    </div>
                  )}
                  {isAdmin && member.role === 'admin' && (
                    <div className="absolute top-2 right-2 z-10">
                      <ActionMenu
                        items={[
                          {
                            label: 'Edit Member',
                            icon: <Edit className="w-4 h-4" />,
                            onClick: () => setShowEditMemberModal(member),
                          },
                        ]}
                      />
                    </div>
                  )}
                </div>
              ))}
            </Grid>
          </div>

          {/* Sounds Section */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-6 relative">
              <h2 className="text-2xl font-semibold">
                  {t('bands.sounds.title')}
              </h2>
              {isMember && (
                <button
                  onClick={() => setShowAddSoundModal(true)}
                  className="btn-outline absolute right-0 flex items-center space-x-2"
                >
                  <Plus className="w-5 h-5" />
                  <span className="hidden md:inline">{t('bands.sounds.addSound')}</span>
                </button>
              )}
            </div>

            <div style={{padding: 0}} className="flex flex-col gap-1">
              <SortableSoundList
                sounds={sounds}
                onSoundsReorder={handleSoundsReorder}
                playingSound={playingSound}
                setPlayingSound={setPlayingSound}
                isMember={isMember}
                onEdit={setShowEditSoundModal}
                onDelete={(id) => setShowDeleteSoundConfirm(id)}
              />
            </div>
          </div>

          {/* Releases Section */}
          {(releases.length > 0 || isMember) && (
            <div className="mb-8">
              <div className="flex justify-between items-center mb-6 relative">
                <h2 className="text-2xl font-semibold">Releases</h2>
                {isMember && (
                  <button
                    onClick={() => setShowAddReleaseModal(true)}
                    className="btn-outline absolute right-0 flex items-center space-x-2"
                  >
                    <Plus className="w-5 h-5" />
                    <span className="hidden md:inline">Add Release</span>
                  </button>
                )}
              </div>
              {releases.length > 0 ? (
                <ReleasesSlider releases={releases} isMember={isMember} onEdit={setShowEditReleaseModal} onDelete={(id) => setShowDeleteReleaseConfirm(id)} />
              ) : (
                <div className="text-center py-12 panel">
                  <p className="text-light/60">No releases added yet.</p>
                  {isMember && (
                    <button
                      onClick={() => setShowAddReleaseModal(true)}
                      className="block mt-4 underline mx-auto"
                    >
                      Add your first release
                    </button>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-8 mt-8">
            <div className="order-2">
              <div className="flex justify-between items-center mb-6 relative">
                <h2 className="text-2xl font-semibold">{t('bands.stories')}</h2>
                {isMember && (
                  <Link
                    to={`/bands/${band.slug}/stories/new`}
                    className="btn-outline absolute right-0 flex items-center space-x-2"
                  >
                    <Plus className="w-5 h-5" />
                    <span className="hidden md:inline">{t('bands.newStory')}</span>
                  </Link>
                )}
              </div>
              <div className="space-y-6">
                {band.stories.length > 0 ? (
                  band.stories.map((story) => (
                    <StoryPanel
                      key={story.id}
                     story={{
                       ...story,
                       band: band
                     }}
                      onClick={() => setSelectedStory(story)}
                      showActions={isMember}
                      onEdit={() => navigate(`/bands/${band.slug}/stories/${story.slug}/edit`)}
                      onDelete={() => {
                        // Handle story deletion
                      }}
                    />
                  ))
                ) : (
                  <div className="text-center py-12 panel">
                    <p className="text-light/60">{t('bands.noStories')}</p>
                    {isMember && (
                      <Link
                        to={`/bands/${band.slug}/stories/new`}
                        className="block mt-4 underline"
                      >
                        {t('bands.shareFirstStory')}
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="md:order-3">
              <div className="flex justify-between items-center mb-6 relative">
                <h2 className="text-2xl font-semibold">{t('bands.events')}</h2>
                {isMember ? (
                  <Link
                    to={`/bands/${band.slug}/events/new`}
                    className="btn-outline absolute right-0 flex items-center space-x-2"
                  >
                    <Plus className="w-5 h-5" />
                    <span className="hidden md:inline">{t('bands.newEvent')}</span>
                  </Link>
                ) : (
                  <Link
                    to={`/book-band/${band.slug}`}
                    className="btn-outline absolute right-0 flex items-center space-x-2"
                  >
                    <Calendar className="w-5 h-5" />
                    <span>{t('bands.bookBand')}</span>
                  </Link>                
                )}
              </div>
              <div className="space-y-6">
                {band.events.length > 0 ? (
                  band.events.map((event) => (
                    <EventPanel
                      key={event.id}
                      event={event}
                      bandSlug={band.slug}
                      showActions={isMember}
                      onEdit={() => navigate(`/bands/${band.slug}/events/${event.slug}/edit`)}
                      onDelete={() => {
                        // Handle event deletion
                      }}
                    />
                  ))
                ) : (
                  <div className="text-center py-12 panel">
                    <p className="text-light/60">{t('bands.noEvents')}</p>
                    {isMember && (
                      <Link
                        to={`/bands/${band.slug}/events/new`}
                        className="block mt-4 underline"
                      >
                        {t('bands.scheduleFirstEvent')}
                      </Link>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {selectedStory && (
          <StoryModal
           story={{
             ...selectedStory,
             band: band
           }}
            isOpen={true}
            onClose={() => setSelectedStory(null)}
          />
        )}

        <ConfirmationDialog
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDelete}
          title={t('bands.deleteBandConfirmTitle')}
          message={
            <div className="space-y-4 text-light/60">
              <p>{t('bands.deleteBandConfirmMessage')} <span className="text-light">{band.name}</span>?</p>
              <p>{t('bands.deleteBandWarning')}</p>
              <ul className="list-disc list-inside space-y-1">
                {deleteBandWarningItems.map((deleteBandWarningItem, index) => (
                    <li key={index}>{deleteBandWarningItem}</li>
                ))}
              </ul>
            </div>
          }
          confirmLabel={t('bands.deleteBand')}
          isDanger={true}
          isLoading={isDeleting}
        />

        <Modal
          isOpen={showInviteModal}
          onClose={() => setShowInviteModal(false)}
          title={t('bands.inviteMember.title')}
        >
          <InviteMemberForm
            bandId={band.id}
            onSuccess={() => {
              setShowInviteModal(false);
              // Reload band details to show new member
              window.location.reload();
            }}
            onCancel={() => setShowInviteModal(false)}
          />
        </Modal>

        <Modal
          isOpen={showAddSoundModal}
          onClose={() => setShowAddSoundModal(false)}
          title={t('bands.sounds.addSound')}
        >
          <SoundForm
            bandId={band.id}
            onSuccess={() => {
              setShowAddSoundModal(false);
              // Reload sounds
              window.location.reload();
            }}
            onCancel={() => setShowAddSoundModal(false)}
          />
        </Modal>

        {showEditSoundModal && (
          <Modal
            isOpen={true}
            onClose={() => setShowEditSoundModal(null)}
            title={t('bands.sounds.editSound')}
          >
            <SoundForm
              bandId={band.id}
              sound={showEditSoundModal}
              onSuccess={() => {
                setShowEditSoundModal(null);
                // Reload sounds
                window.location.reload();
              }}
              onCancel={() => setShowEditSoundModal(null)}
            />
          </Modal>
        )}

        <ConfirmationDialog
          isOpen={!!showDeleteSoundConfirm}
          onClose={() => setShowDeleteSoundConfirm(null)}
          onConfirm={() => showDeleteSoundConfirm && handleDeleteSound(showDeleteSoundConfirm)}
          title={t('bands.sounds.deleteSound')}
          message={t('bands.sounds.deleteSoundConfirm')}
          confirmLabel={t('bands.sounds.deleteSound')}
          isDanger={true}
        />

        <ConfirmationDialog
          isOpen={!!showRemoveMemberConfirm}
          onClose={() => setShowRemoveMemberConfirm(null)}
          onConfirm={handleRemoveMember}
          title={t('bands.members.removeMemberConfirmTitle')}
          message={
            <div className="space-y-4 text-light/60">
              <p>{t('bands.members.removeMemberConfirmMessage', { name: showRemoveMemberConfirm?.name })}</p>
              <p>{t('bands.members.removeMemberWarning')}</p>
            </div>
          }
          confirmLabel={t('bands.members.removeMember')}
          isDanger={true}
        />

        <FanAreaModal
          band={band}
          isOpen={showFanAreaModal}
          onClose={() => setShowFanAreaModal(false)}
        />

        {showEditMemberModal && (
          <Modal
            isOpen={true}
            onClose={() => setShowEditMemberModal(null)}
            title="Edit Band Member"
          >
            <EditMemberForm
              bandId={band.id}
              member={showEditMemberModal}
              totalAdmins={band.members.filter(m => m.role === 'admin').length}
              onSuccess={() => {
                setShowEditMemberModal(null);
                // Reload band details to show updated member info
                window.location.reload();
              }}
              onCancel={() => setShowEditMemberModal(null)}
            />
          </Modal>
        )}

        <Modal
          isOpen={showAddReleaseModal}
          onClose={() => setShowAddReleaseModal(false)}
          title="Add Release"
        >
          <ReleaseForm
            bandId={band.id}
            onSuccess={() => {
              setShowAddReleaseModal(false);
              // Reload releases
              window.location.reload();
            }}
            onCancel={() => setShowAddReleaseModal(false)}
          />
        </Modal>

        {showEditReleaseModal && (
          <Modal
            isOpen={true}
            onClose={() => setShowEditReleaseModal(null)}
            title="Edit Release"
          >
            <ReleaseForm
              bandId={band.id}
              release={showEditReleaseModal}
              onSuccess={() => {
                setShowEditReleaseModal(null);
                // Reload releases
                window.location.reload();
              }}
              onCancel={() => setShowEditReleaseModal(null)}
            />
          </Modal>
        )}

        <ConfirmationDialog
          isOpen={!!showDeleteReleaseConfirm}
          onClose={() => setShowDeleteReleaseConfirm(null)}
          onConfirm={() => showDeleteReleaseConfirm && handleDeleteRelease(showDeleteReleaseConfirm)}
          title="Delete Release"
          message="Are you sure you want to delete this release? This action cannot be undone."
          confirmLabel="Delete Release"
          isDanger={true}
        />

        <QRFlyerModal
          band={band}
          isOpen={showQRFlyerModal}
          onClose={() => setShowQRFlyerModal(false)}
        />
      </div>
    </>
  );
}

export default BandDetails;